/**
 * BusquedaGrupo Component
 * 
 * Componente para buscar grupos por campus y tipo de evaluación.
 * Muestra tabla de resultados con grupos disponibles.
 * Integrado con APIs del backend.
 * 
 * Basado en Image 16 del diseño
 */

import React, { useState, useEffect } from "react";
import styles from "./RegistroCalificaciones.module.css";
import { getGruposAsesor, getCampusDisponibles, getTiposEvaluacion } from "../../api/calificaciones";

function BusquedaGrupo({ onSeleccionarGrupo, infoAsesor, infoUsuario, campusSeleccionado }) {
  // Eliminado filtro de campus
  const [tipoEvaluacion, setTipoEvaluacion] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campusOptions, setCampusOptions] = useState([]);
  const [tiposEvaluacion, setTiposEvaluacion] = useState([]);
  const [loadingCampus, setLoadingCampus] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  // Eliminado efecto de carga de campus

  // Cargar tipos de evaluación
  useEffect(() => {
    const cargarTipos = async () => {
      try {
        const data = await getTiposEvaluacion();
        setTiposEvaluacion(data.tipos_evaluacion || []);
      } catch (error) {
        console.error('[BusquedaGrupo] Error al cargar tipos evaluación:', error);
        // Fallback
        setTiposEvaluacion([
          { value: 'PARCIAL1', label: 'Parcial 1' },
          { value: 'PARCIAL2', label: 'Parcial 2' },
          { value: 'PARCIAL3', label: 'Parcial 3' },
          { value: 'FINAL', label: 'Final' },
          { value: 'EXTRAORDINARIO', label: 'Extraordinario' }
        ]);
      }
    };
    
    cargarTipos();
  }, []);

  // Cargar grupos cuando hay asesor disponible o al reintentar
  useEffect(() => {
    if (infoAsesor?.id_asesor) {
      handleBuscar();
    }
  }, [infoAsesor, retryCount]);

  // Eliminada sincronización de campus seleccionado

  const handleBuscar = async (campusOverride) => {
    // Verificar que tenemos un asesor
    if (!infoAsesor?.id_asesor) {
      setErrorMessage('No se detectó un asesor asociado a este usuario. Solo los asesores pueden ver sus grupos.');
      setGrupos([]);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    try {
      const data = await getGruposAsesor({
        id_asesor: infoAsesor.id_asesor,
        search: busqueda || undefined
      });
      console.log('[BusquedaGrupo] Respuesta grupos:', data);
      if (data.debug) {
        console.log('[BusquedaGrupo] Debug info:', data.debug);
      }
      setGrupos(data.grupos || []);
      if (data.grupos?.length > 0 && data.debug?.metodo_busqueda === 'nombre_email_alternativo') {
        setInfoMessage('Los grupos se encontraron a través de una búsqueda por nombre. Se recomienda que el administrador unifique los registros duplicados de asesor.');
      }
      if (data.grupos?.length === 0) {
        // Construir mensaje descriptivo según la situación
        let msg = '';
        const debug = data.debug || {};
        
        if (debug.metodo_busqueda === 'nombre_email_alternativo') {
          msg = 'Se encontraron registros de asesor similares pero ninguno tiene grupos activos asignados. Contacte al administrador para verificar la asignación de grupos.';
        } else if (debug.asesores_similares?.length > 0 && debug.total_grupos_asesor_sin_filtro === 0) {
          const nombres = debug.asesores_similares.map(a => a.nombre_completo).join(', ');
          msg = `Existen registros de asesor con nombre similar (${nombres}) pero los grupos están asignados a un registro diferente. Contacte al administrador para unificar los registros duplicados.`;
        } else if (debug.asesor_email === 'Sin email') {
          msg = 'El registro de asesor no tiene correo electrónico configurado, lo que dificulta la vinculación automática. Contacte al administrador para actualizar el correo del asesor.';
        } else if (debug.total_grupos_asesor_sin_filtro === 0) {
          msg = 'No se encontraron grupos asignados a este asesor. Verifique que los grupos estén correctamente asignados en la administración de grupos.';
        } else {
          msg = 'No se encontraron grupos activos. Es posible que todos los grupos estén cerrados o cancelados.';
        }
        
        setErrorMessage(msg);
      }
    } catch (error) {
      console.error('[BusquedaGrupo] Error al buscar grupos:', error);
      setErrorMessage('Error al buscar grupos. Intente nuevamente.');
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler para reintentar la carga de datos
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const handleSeleccionar = (grupo) => {
    if (!tipoEvaluacion) {
      alert('Por favor seleccione un tipo de evaluación');
      return;
    }
    onSeleccionarGrupo(grupo, tipoEvaluacion, tiposEvaluacion);
  };

  return (
    <div className={styles.busquedaContainer}>
      {/* Card de Búsqueda de Grupo */}
      <div className={styles.busquedaCard}>
        <div className={styles.busquedaHeader}>
          <div className={styles.busquedaIcono}>
            <span>🔍</span>
          </div>
          <h2 className={styles.busquedaTitulo}>Búsqueda de Grupo</h2>
        </div>

        {/* Filtros */}
        <div className={styles.filtrosContainer}>
          <div className={styles.filtroGroup}>
            <label className={styles.filtroLabel}>Tipo de Evaluación</label>
            <select 
              className={styles.filtroSelect}
              value={tipoEvaluacion}
              onChange={(e) => setTipoEvaluacion(e.target.value)}
            >
              <option value="">-- Seleccionar --</option>
              {tiposEvaluacion.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className={styles.searchBarContainer}>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar Grupo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
            />
          </div>
          <button 
            className={styles.buscarButton}
            onClick={handleBuscar}
            disabled={loading || !infoAsesor?.id_asesor}
          >
            {loading ? 'BUSCANDO...' : 'BUSCAR'}
          </button>
        </div>

        {/* Mensaje de error o información */}
        {errorMessage && (
          <div className={styles.errorMessage}>
            {errorMessage}
            <button 
              onClick={handleRetry}
              style={{ 
                marginLeft: '12px', 
                padding: '4px 12px', 
                cursor: 'pointer',
                border: '1px solid #ccc',
                borderRadius: '4px',
                background: '#f5f5f5',
                fontSize: '13px'
              }}
            >
              🔄 Reintentar
            </button>
          </div>
        )}

        {/* Mensaje informativo (match alternativo exitoso) */}
        {infoMessage && !errorMessage && (
          <div style={{
            padding: '10px 16px',
            margin: '8px 0',
            background: '#e8f4fd',
            border: '1px solid #b3d9f2',
            borderRadius: '6px',
            color: '#1a5276',
            fontSize: '13px',
            lineHeight: '1.5'
          }}>
            ℹ️ {infoMessage}
          </div>
        )}

        {/* Mensaje si no hay asesor */}
        {!infoAsesor?.id_asesor && !loading && (
          <div className={styles.warningMessage}>
            <div style={{ marginBottom: '8px' }}>
              <strong>⚠️ No se pudo vincular su cuenta con un registro de asesor</strong>
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              Esto puede ocurrir porque:
              <ul style={{ margin: '6px 0', paddingLeft: '20px' }}>
                <li>Su nombre de usuario no coincide con ningún asesor registrado</li>
                <li>Su correo electrónico no está asociado a un registro de asesor</li>
                <li>El nombre en su cuenta tiene un orden o escritura diferente al del registro de asesor</li>
              </ul>
              Contacte al administrador para vincular su cuenta de usuario con su registro de asesor.
            </div>
          </div>
        )}
      </div>

      {/* Tabla de Resultados */}
      {(grupos.length > 0 || loading) && (
        <div className={styles.tablaContainer}>
          <table className={styles.tabla}>
            <thead>
              <tr className={styles.tablaHeader}>
                <th>Grupo</th>
                <th>Campus</th>
                <th>Programa</th>
                <th>Ciclo Escolar</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className={styles.tablaCargando}>Cargando grupos...</td>
                </tr>
              ) : (
                grupos.map((grupo) => (
                  <tr 
                    key={grupo.id_grupo} 
                    className={styles.tablaFila}
                    onClick={() => handleSeleccionar(grupo)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{grupo.codigo} - {grupo.nombre}</td>
                    <td>{grupo.campus}</td>
                    <td>{
                      grupo.programa_academico && grupo.programa_academico !== 'Sin programa'
                        ? grupo.programa_academico
                        : grupo.programa
                    }</td>
                    <td>{grupo.ciclo}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BusquedaGrupo;
